import React from 'react';
import theme, { cn } from '../../styles/theme';

// ✅ Sin useState, sin ProductoOpcionesModal — el modal lo maneja ProductoList
// La prop onVerOpciones viene del padre y abre el modal allá

const ProductoItem = ({ producto, categorias, onEdit, onVerOpciones }) => {

  const getBadgeStyles = (estado) =>
    estado === 'ACTIVO' ? theme.classes.status.active : theme.classes.status.inactive;

  const getStatusIcon = (estado) =>
    estado === 'ACTIVO' ? (
      <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ) : (
      <svg className="w-3 h-3 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    );

  const getImageUrl = (imagen) => {
    if (!imagen) return null;
    if (imagen.startsWith('data:') || imagen.startsWith('http')) return imagen;
    return `data:image/jpeg;base64,${imagen}`;
  };

  const ProductImage = ({ imagen, nombre }) => {
    const imageUrl = getImageUrl(imagen);
    if (!imageUrl) {
      return (
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      );
    }
    return (
      <div className="relative group">
        <img src={imageUrl} alt={nombre || 'Producto'}
          className={cn("w-16 h-16 object-cover rounded-lg border border-gray-200", theme.shadows.sm, "group-hover:shadow-md", theme.transitions.default)}
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center absolute top-0 left-0" style={{ display: 'none' }}>
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <div className={cn("absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100", theme.transitions.default)}>
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <tr className={cn(theme.classes.table.row)}>

      {/* ID */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded inline-block">
          #{producto.Id_producto}
        </div>
      </td>

      {/* Imagen */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center justify-center">
          <ProductImage imagen={producto.Imagen} nombre={producto.Nombre_producto} />
        </div>
      </td>

      {/* Nombre */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-semibold text-gray-900">{producto.Nombre_producto}</div>
        <div className="text-xs text-gray-500 mt-1">Producto</div>
      </td>

      {/* Descripción */}
      <td className="px-6 py-4">
        <div className="max-w-xs">
          <p className="text-sm text-gray-900 leading-relaxed line-clamp-2">{producto.Descripcion || 'Sin descripción'}</p>
          <div className="text-xs text-gray-500 mt-1">Descripción</div>
        </div>
      </td>

      {/* Precio */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="p-2 bg-green-100 rounded-lg mr-3">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">S/. {parseFloat(producto.Precio || 0).toFixed(2)}</div>
            <div className="text-xs text-gray-500 mt-1">Precio</div>
          </div>
        </div>
      </td>

      {/* Categoría */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="p-2 bg-gray-100 rounded-lg mr-3">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div>
            <span className={cn(theme.classes.badge.primary, "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium")}>
              {producto.Nombre_categoria}
            </span>
            <div className="text-xs text-gray-500 mt-1">Categoría</div>
          </div>
        </div>
      </td>

      {/* Estado */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={cn("inline-flex items-center px-3 py-1 rounded-full text-xs font-medium", getBadgeStyles(producto.Estado))}>
          {getStatusIcon(producto.Estado)}
          {producto.Estado}
        </span>
      </td>

      {/* Acciones */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2">

          {/* Editar */}
          <button onClick={() => onEdit(producto)}
            className={cn("group relative inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 hover:border-gray-300 focus:outline-none", theme.transitions.default)}
            title="Editar producto">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Editar
            <span className={cn("absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap", theme.transitions.default)}>
              Modificar este Producto
            </span>
          </button>

          {/* ✅ Botón Opciones — llama al padre */}
          <button onClick={() => onVerOpciones && onVerOpciones(producto)}
            className="group relative inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 hover:border-purple-300 focus:outline-none transition-colors"
            title="Gestionar opciones del producto">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Opciones
            <span className={cn("absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap", theme.transitions.default)}>
              Gestionar opciones
            </span>
          </button>

        </div>
      </td>
    </tr>
  );
};

export default ProductoItem;